/*───────────────────────────────────────────────────────────────
 📄 File: components/ui/SearchableSelect.tsx
 🧩 Description:
    Searchable dropdown that replaces <select> with a combobox-
    style input + filtered list.
    Features:
    - Searchable via text input (no external dependencies)
    - Keyboard navigation (↑ ↓ Enter Escape)
    - Accessible (aria attributes)
    - Compatible with the platform color tokens
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export interface SelectOption {
    /** The value stored in state (string) */
    value: string;
    /** The label displayed to the user */
    label: string;
    /** Optional secondary text shown smaller beside label */
    subLabel?: string;
    disabled?: boolean;
}

interface SearchableSelectProps {
    id?: string;
    value: string | null;
    onChange: (value: string | null) => void;
    options: SelectOption[];
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    loading?: boolean;
    clearable?: boolean;
    className?: string;
}

export function SearchableSelect({
    id,
    value,
    onChange,
    options,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    disabled = false,
    loading = false,
    clearable = true,
    className = '',
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const instanceId = useId();
    const controlId = id ?? `ss-${instanceId}`;

    // Derived: selected option label
    const selected = options.find(o => o.value === value);

    // Filter options by query
    const filtered = query.trim()
        ? options.filter(o =>
            o.label.toLowerCase().includes(query.toLowerCase()) ||
            (o.subLabel?.toLowerCase().includes(query.toLowerCase()))
        )
        : options;

    // Close on outside click
    useEffect(() => {
        function handle(e: MouseEvent) {
            if (!containerRef.current?.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
            }
        }
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    // Focus input when opening
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 10);
            setHighlighted(0);
        }
    }, [open]);

    // Scroll highlighted into view
    useEffect(() => {
        const el = listRef.current?.children[highlighted] as HTMLElement | undefined;
        el?.scrollIntoView({ block: 'nearest' });
    }, [highlighted]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!open) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } return; }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlighted(h => Math.min(h + 1, filtered.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlighted(h => Math.max(h - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filtered[highlighted]) {
                    onChange(filtered[highlighted].value);
                    setOpen(false);
                    setQuery('');
                }
                break;
            case 'Escape':
                setOpen(false);
                setQuery('');
                break;
        }
    }, [open, filtered, highlighted, onChange]);

    const handleSelect = (opt: SelectOption) => {
        if (opt.disabled) return;
        onChange(opt.value);
        setOpen(false);
        setQuery('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    const baseInputClass = `
        w-full flex items-center justify-between gap-2 px-3 py-2 text-sm
        bg-background border border-input rounded-md
        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors cursor-pointer text-left
        ${open ? 'ring-2 ring-primary/20 border-primary' : ''}
        ${className}
    `.trim();

    return (
        <div
            ref={containerRef}
            className="relative"
            onKeyDown={handleKeyDown}
        >
            {/* Trigger button */}
            <button
                id={controlId}
                type="button"
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                disabled={disabled || loading}
                onClick={() => setOpen(o => !o)}
                className={baseInputClass}
            >
                <span className={`flex-1 truncate text-left ${!selected ? 'text-muted-foreground' : ''}`}>
                    {loading
                        ? 'Loading…'
                        : selected
                            ? (
                                <span className="flex items-center gap-2">
                                    <span>{selected.label}</span>
                                    {selected.subLabel && (
                                        <span className="text-xs text-muted-foreground">({selected.subLabel})</span>
                                    )}
                                </span>
                            )
                            : placeholder
                    }
                </span>
                <span className="flex items-center gap-1 shrink-0">
                    {clearable && value && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            onClick={handleClear}
                            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-3 w-3" />
                        </span>
                    )}
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                </span>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-border">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={e => { setQuery(e.target.value); setHighlighted(0); }}
                                placeholder={searchPlaceholder}
                                className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-input rounded focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                        </div>
                    </div>

                    {/* Options list */}
                    <ul
                        ref={listRef}
                        role="listbox"
                        className="max-h-56 overflow-y-auto py-1"
                    >
                        {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-muted-foreground text-center">
                                No results found
                            </li>
                        ) : (
                            filtered.map((opt, idx) => (
                                <li
                                    key={opt.value}
                                    role="option"
                                    aria-selected={opt.value === value}
                                    onClick={() => handleSelect(opt)}
                                    className={`
                                        flex items-center justify-between px-3 py-2 text-sm cursor-pointer
                                        ${opt.disabled ? 'opacity-40 cursor-not-allowed' : ''}
                                        ${idx === highlighted ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
                                        ${opt.value === value ? 'font-medium' : ''}
                                    `}
                                >
                                    <span className="truncate">{opt.label}</span>
                                    {opt.subLabel && (
                                        <span className="ml-2 text-xs text-muted-foreground shrink-0">
                                            {opt.subLabel}
                                        </span>
                                    )}
                                    {opt.value === value && (
                                        <span className="ml-auto pl-2 text-primary shrink-0">✓</span>
                                    )}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default SearchableSelect;
